/**
 * What a new call does while this person is already on a call, as pure rules.
 *
 * The CHOICE is the person's own and lives on the server (owner's decision,
 * 2026-11-02): `knock` rings this browser with a call-waiting tone, `voicemail`
 * and `queue` turn the new call down at once and leave the rest to the server.
 * The same three words are read by the phone app, so a value this build does
 * not know is read as the default rather than as a fourth case.
 *
 * Nothing here imports React or the Voice SDK. The provider asks these
 * questions at the moment an offer arrives and when a button is drawn.
 */
import { isBusyMode, type BusyMode } from "../../../lib/callBusyMode";
import { isLiveCall, type CallPhase, type CallState } from "../callState";

export type { BusyMode };

/** The owner's default, for a person who never chose. */
export const DEFAULT_BUSY_MODE: BusyMode = "knock";

/** Any stored or sent value, narrowed. Unknown, null or missing is the default. */
export function parseBusyMode(value: unknown): BusyMode {
  return isBusyMode(value) ? value : DEFAULT_BUSY_MODE;
}

/**
 * Is this window busy for a new offer?
 *
 * Busy is more than a live call. A call on hold is still a call the person is
 * on, even while the screen in front shows the after-call form of the second
 * one, and an offer already ringing on a card is one offer too many. Only a
 * window with nothing up - or only the screen of a call that is over - is free.
 */
export function isBusy(phase: CallPhase, hasHeld: boolean, knocking: boolean): boolean {
  if (hasHeld || knocking) return true;
  return !(phase === "idle" || phase === "ended" || phase === "error");
}

/** What to do with an offer that arrived while busy. */
export type BusyVerdict = "knock" | "turn-down";

/**
 * Knock, or turn the offer down at once.
 *
 * Turned down whatever the choice when another offer is already ringing: the
 * card that asks "answer?" is about the first one, and a second card on top of
 * it would be a question nobody can tell apart from the first. Today's
 * behaviour there is kept on purpose.
 */
export function busyVerdict(args: {
  mode: BusyMode;
  phase: CallPhase;
  /** A knock card is already up for an earlier offer. */
  knocking: boolean;
}): BusyVerdict {
  if (args.mode !== "knock") return "turn-down";
  if (args.phase === "incoming") return "turn-down";
  if (args.knocking) return "turn-down";
  return "knock";
}

/** The slice of the call in front that decides whether it can be held. */
export type HoldSlice = Pick<
  CallState,
  "phase" | "callLogId" | "conference" | "takeover" | "direction" | "incoming"
>;

/**
 * Can the server put this call's customer on hold, the way the Hold button does?
 *
 * Two shapes can: an outgoing call bridged as a conference, and an incoming
 * caller who was PARKED in a room (the offer arrived with a leg sid and no row
 * id - see `IncomingCall.legSid`). The server's hold route reads the customer's
 * leg from the right column for each. A `<Dial>` call has no room to hold
 * anybody in, and a takeover's row is the AI's call, not a bridge of ours.
 */
export function canHold(call: HoldSlice): boolean {
  if (call.phase !== "active" || !call.callLogId || call.takeover) return false;
  if (call.conference) return true;
  return call.direction === "inbound" && !!call.incoming?.legSid;
}

/**
 * Whether the knock card may offer Answer, and if not, the reason it shows.
 *
 * - `ok`: answering holds the call in front (if there is a live one) and takes
 *   the new call.
 * - `not_yet`: the call in front is still being set up. There is no customer
 *   in a room to hold yet.
 * - `cannot_hold`: the call in front has no hold. Answering would mean hanging
 *   up on that customer, which is not this button's to decide.
 * - `one_on_hold`: one call is already on hold and the call in front is live.
 *   Two people on hold music and a third on the line is past what this window
 *   can show honestly.
 */
export type AnswerBlock = "ok" | "not_yet" | "cannot_hold" | "one_on_hold";

export function knockAnswerBlock(front: HoldSlice, hasHeld: boolean): AnswerBlock {
  // Nothing live in front: the call before is over, so the new one simply
  // takes its place, whether or not another one waits on hold.
  if (!isLiveCall(front.phase)) return "ok";
  if (hasHeld) return "one_on_hold";
  if (front.phase !== "active") return "not_yet";
  return canHold(front) ? "ok" : "cannot_hold";
}

/**
 * Whether the held call can be taken back right now.
 *
 * With nothing live in front it always can. With a live call in front, taking
 * the held one back means holding the one in front instead - a swap - which
 * only works when that one can be held too. Otherwise the person finishes the
 * call in front first; the held strip says so.
 */
export function canTakeBack(front: HoldSlice): boolean {
  if (!isLiveCall(front.phase)) return true;
  return canHold(front);
}
